import { useState } from 'react';
import { Popconfirm, Tooltip } from 'antd';
import {
  CheckCircleFilled,
  ClockCircleOutlined,
  CloseCircleFilled,
  CloseOutlined,
  DownOutlined,
  FileTextOutlined,
  FileWordOutlined,
  FilePdfOutlined,
  FileUnknownOutlined,
  LoadingOutlined,
  PaperClipOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  FileProcessingStatus,
  InquiryFileStatus,
} from '@/types/chat/chat';
import styles from '@/styles/ChatWindow.module.scss';

const isTerminal = (status: FileProcessingStatus) =>
  status === 'finished_processing' || status === 'failed';

const getFileIcon = (type: string) => {
  switch ((type || '').toLowerCase()) {
    case 'pdf':
      return <FilePdfOutlined className={styles.fileTypeIcon} />;
    case 'docx':
    case 'doc':
      return <FileWordOutlined className={styles.fileTypeIcon} />;
    case 'txt':
      return <FileTextOutlined className={styles.fileTypeIcon} />;
    default:
      return <FileUnknownOutlined className={styles.fileTypeIcon} />;
  }
};

interface InquiryFilesProps {
  inquiryId: string;
  files: InquiryFileStatus[];
  loading: boolean;
  removingId: string | null;
  onRemove: (fileId: string) => Promise<void>;
}

const InquiryFiles = ({
  inquiryId,
  files,
  loading,
  removingId,
  onRemove,
}: InquiryFilesProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  if (!inquiryId || (!loading && files.length === 0)) {
    return null;
  }

  const processingCount = files.filter(f => !isTerminal(f.status)).length;
  const failedCount = files.filter(f => f.status === 'failed').length;
  const readyCount = files.filter(
    f => f.status === 'finished_processing',
  ).length;

  const renderSummary = () => {
    if (loading && files.length === 0) {
      return (
        <span className={`${styles.fileSummary} ${styles.fileStatusProcessing}`}>
          <LoadingOutlined />
          <span>{t('home.chat.files.loading')}</span>
        </span>
      );
    }
    return (
      <span className={styles.fileSummary}>
        <Tooltip title={t('home.chat.files.statuses.finished_processing')}>
          <span className={styles.fileStatusDone}>
            <CheckCircleFilled /> {readyCount}
          </span>
        </Tooltip>
        <Tooltip title={t('home.chat.files.statuses.failed')}>
          <span className={styles.fileStatusFailed}>
            <CloseCircleFilled /> {failedCount}
          </span>
        </Tooltip>
        <Tooltip title={t('home.chat.files.statuses.processing')}>
          <span className={styles.fileStatusProcessing}>
            {processingCount > 0 ? <LoadingOutlined /> : <ClockCircleOutlined />}{' '}
            {processingCount}
          </span>
        </Tooltip>
      </span>
    );
  };

  const renderStatusIcon = (file: InquiryFileStatus) => {
    switch (file.status) {
      case 'finished_processing':
        return (
          <Tooltip title={t('home.chat.files.statuses.finished_processing')}>
            <CheckCircleFilled className={styles.fileStatusDone} />
          </Tooltip>
        );
      case 'failed':
        return (
          <Tooltip title={t('home.chat.files.uploadFailed')}>
            <CloseCircleFilled className={styles.fileStatusFailed} />
          </Tooltip>
        );
      case 'processing':
        return (
          <Tooltip
            title={`${t('home.chat.files.statuses.processing')} ${file.progress}%`}
          >
            <LoadingOutlined className={styles.fileStatusProcessing} />
          </Tooltip>
        );
      case 'pending':
      default:
        return (
          <Tooltip title={t('home.chat.files.statuses.pending')}>
            <ClockCircleOutlined className={styles.fileStatusPending} />
          </Tooltip>
        );
    }
  };

  return (
    <div className={styles.inquiryFiles}>
      <button
        type="button"
        className={styles.inquiryFilesHeader}
        onClick={() => setExpanded(prev => !prev)}
        aria-expanded={expanded}
      >
        <PaperClipOutlined />
        <span className={styles.inquiryFilesTitle}>
          {t('home.chat.files.title')}
        </span>
        {files.length > 0 && (
          <span className={styles.inquiryFilesCount}>{files.length}</span>
        )}
        {renderSummary()}
        <DownOutlined
          className={`${styles.inquiryFilesCaret} ${expanded ? styles.inquiryFilesCaretOpen : ''}`}
        />
      </button>
      {expanded && files.length > 0 && (
        <div className={styles.inquiryFilesList}>
          {files.map(file => (
            <div key={file.id} className={styles.fileChip}>
              {getFileIcon(file.type)}
              <Tooltip title={file.name}>
                <span className={styles.fileName}>{file.name}</span>
              </Tooltip>
              {removingId === file.id ? (
                <LoadingOutlined className={styles.fileStatusProcessing} />
              ) : (
                renderStatusIcon(file)
              )}
              <Popconfirm
                title={t('home.chat.files.removeConfirm')}
                okText={t('home.chat.files.removeOk')}
                cancelText={t('home.chat.files.removeCancel')}
                okButtonProps={{ danger: true }}
                onConfirm={() => onRemove(file.id)}
                disabled={removingId === file.id}
              >
                <Tooltip title={t('home.chat.files.remove')}>
                  <button
                    type="button"
                    className={styles.fileRemoveBtn}
                    disabled={removingId === file.id}
                    aria-label={t('home.chat.files.remove')}
                  >
                    <CloseOutlined />
                  </button>
                </Tooltip>
              </Popconfirm>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default InquiryFiles;
